import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { 
  Star, MessageSquare, Check, X, Eye, EyeOff, Search, 
  RefreshCw, Trash2, Award, Clock, User, Loader2 
} from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './ui/alert-dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TestimonialManagement = ({ token }) => {
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filter, setFilter] = useState('all'); // all, pending, approved, featured
  const [selectedTestimonial, setSelectedTestimonial] = useState(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    fetchTestimonials();
    fetchStats();
  }, []);

  const fetchTestimonials = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/testimonials`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTestimonials(response.data);
    } catch (error) {
      console.error('Failed to fetch testimonials:', error);
      toast.error('Erreur lors du chargement des témoignages');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/testimonials/stats`);
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  const handleApprove = async (testimonialId, approve) => {
    setActionLoading(true);
    try {
      await axios.put(
        `${API}/admin/testimonials/${testimonialId}`,
        { is_approved: approve },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(approve ? 'Témoignage approuvé' : 'Témoignage rejeté');
      fetchTestimonials();
      fetchStats();
    } catch (error) {
      console.error('Failed to update testimonial:', error);
      toast.error('Erreur lors de la mise à jour');
    } finally {
      setActionLoading(false);
    }
  };

  const handleFeature = async (testimonialId, feature) => {
    setActionLoading(true);
    try {
      await axios.put(
        `${API}/admin/testimonials/${testimonialId}`,
        { is_featured: feature },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(feature ? 'Témoignage mis en avant' : 'Témoignage retiré de la mise en avant');
      fetchTestimonials();
      fetchStats();
    } catch (error) {
      console.error('Failed to update testimonial:', error);
      toast.error('Erreur lors de la mise à jour');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedTestimonial) return;
    
    setActionLoading(true);
    try {
      await axios.delete(`${API}/admin/testimonials/${selectedTestimonial.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Témoignage supprimé');
      setShowDeleteDialog(false);
      setSelectedTestimonial(null);
      fetchTestimonials();
      fetchStats();
    } catch (error) {
      console.error('Failed to delete testimonial:', error);
      toast.error('Erreur lors de la suppression');
    } finally {
      setActionLoading(false);
    }
  };

  const filteredTestimonials = testimonials.filter(t => {
    const matchesSearch = 
      t.comment?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      t.display_name?.toLowerCase().includes(searchTerm.toLowerCase());
    
    if (filter === 'pending') return matchesSearch && !t.is_approved;
    if (filter === 'approved') return matchesSearch && t.is_approved;
    if (filter === 'featured') return matchesSearch && t.is_featured;
    return matchesSearch;
  });

  const StarDisplay = ({ rating }) => (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map((star) => (
        <Star
          key={star}
          className={`h-4 w-4 ${
            star <= rating ? 'fill-yellow-400 text-yellow-400' : 'fill-gray-200 text-gray-300'
          }`}
        />
      ))}
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-2">
                <MessageSquare className="h-5 w-5 text-brand-600" />
                <div>
                  <p className="text-2xl font-bold">{stats.total}</p>
                  <p className="text-xs text-gray-500">Total</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-2">
                <Clock className="h-5 w-5 text-orange-500" />
                <div>
                  <p className="text-2xl font-bold">{stats.pending}</p>
                  <p className="text-xs text-gray-500">En attente</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-2">
                <Check className="h-5 w-5 text-green-500" />
                <div>
                  <p className="text-2xl font-bold">{stats.approved}</p>
                  <p className="text-xs text-gray-500">Approuvés</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-2">
                <Award className="h-5 w-5 text-purple-500" />
                <div>
                  <p className="text-2xl font-bold">{stats.featured}</p>
                  <p className="text-xs text-gray-500">Mis en avant</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-2">
                <Star className="h-5 w-5 text-yellow-400" />
                <div>
                  <p className="text-2xl font-bold">{stats.average_rating || '-'}</p>
                  <p className="text-xs text-gray-500">Note moyenne</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <Card>
        <CardContent className="pt-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Rechercher un témoignage..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="px-3 py-2 border rounded-md text-sm"
            >
              <option value="all">Tous</option>
              <option value="pending">En attente</option>
              <option value="approved">Approuvés</option>
              <option value="featured">Mis en avant</option>
            </select>
            <Button onClick={fetchTestimonials} variant="outline">
              <RefreshCw className="h-4 w-4 mr-2" />
              Actualiser
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Testimonials List */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MessageSquare className="h-5 w-5 text-brand-600" />
            Témoignages ({filteredTestimonials.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-brand-600" />
            </div>
          ) : filteredTestimonials.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              Aucun témoignage trouvé
            </div>
          ) : (
            <div className="space-y-4">
              {filteredTestimonials.map((testimonial) => (
                <div
                  key={testimonial.id}
                  className={`border rounded-lg p-4 transition-colors ${
                    !testimonial.is_approved ? 'border-orange-200 bg-orange-50/50' : 
                    testimonial.is_featured ? 'border-purple-200 bg-purple-50/50' : ''
                  }`}
                >
                  <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2 mb-2">
                        <StarDisplay rating={testimonial.rating} />
                        {!testimonial.is_approved && (
                          <span className="px-2 py-0.5 bg-orange-100 text-orange-700 text-xs rounded-full font-medium">
                            En attente
                          </span>
                        )}
                        {testimonial.is_approved && (
                          <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full font-medium">
                            Approuvé
                          </span>
                        )}
                        {testimonial.is_featured && (
                          <span className="px-2 py-0.5 bg-purple-100 text-purple-700 text-xs rounded-full font-medium">
                            Mis en avant
                          </span>
                        )}
                      </div>
                      
                      <p className="text-gray-700 mb-3 italic">"{testimonial.comment}"</p>
                      
                      <div className="flex items-center gap-4 text-sm text-gray-500">
                        <div className="flex items-center gap-1">
                          <User className="h-4 w-4" />
                          <span>{testimonial.display_name}</span>
                        </div>
                        <span className="capitalize">
                          {testimonial.role === 'buyer' ? 'Acheteur' : 'Vendeur'}
                        </span>
                        {testimonial.created_at && (
                          <span>
                            {new Date(testimonial.created_at).toLocaleDateString('fr-FR')}
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex flex-wrap gap-2">
                      {!testimonial.is_approved ? (
                        <>
                          <Button
                            size="sm"
                            onClick={() => handleApprove(testimonial.id, true)}
                            disabled={actionLoading}
                            className="bg-green-600 hover:bg-green-700 text-white"
                          >
                            <Check className="h-4 w-4 mr-1" />
                            Approuver
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleApprove(testimonial.id, false)}
                            disabled={actionLoading}
                            className="border-red-500 text-red-600 hover:bg-red-50"
                          >
                            <X className="h-4 w-4 mr-1" />
                            Rejeter
                          </Button>
                        </>
                      ) : (
                        <>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleFeature(testimonial.id, !testimonial.is_featured)}
                            disabled={actionLoading}
                            className={testimonial.is_featured ? 'border-purple-500 text-purple-600' : ''}
                          >
                            {testimonial.is_featured ? (
                              <>
                                <EyeOff className="h-4 w-4 mr-1" />
                                Retirer
                              </>
                            ) : (
                              <>
                                <Award className="h-4 w-4 mr-1" />
                                Mettre en avant
                              </>
                            )}
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleApprove(testimonial.id, false)}
                            disabled={actionLoading}
                            className="border-orange-500 text-orange-600 hover:bg-orange-50"
                          >
                            <EyeOff className="h-4 w-4 mr-1" />
                            Masquer
                          </Button>
                        </>
                      )}
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setSelectedTestimonial(testimonial);
                          setShowDeleteDialog(true);
                        }}
                        disabled={actionLoading}
                        className="border-red-500 text-red-600 hover:bg-red-50"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Delete Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-red-600">
              <Trash2 className="h-5 w-5" />
              Supprimer ce témoignage ?
            </AlertDialogTitle>
            <AlertDialogDescription>
              {selectedTestimonial && (
                <div className="space-y-3 pt-2">
                  <p>Vous êtes sur le point de supprimer définitivement ce témoignage :</p>
                  <div className="bg-gray-50 border rounded p-3 space-y-1">
                    <p><strong>De:</strong> {selectedTestimonial.display_name}</p>
                    <p className="italic">"{selectedTestimonial.comment?.substring(0, 100)}..."</p>
                  </div>
                  <div className="bg-red-50 border border-red-200 p-3 rounded text-sm text-red-800">
                    <strong>⚠️ Attention:</strong> Cette action est irréversible.
                  </div>
                </div>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={actionLoading}>Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
              disabled={actionLoading}
            >
              {actionLoading ? 'Suppression...' : 'Supprimer'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default TestimonialManagement;
